import { Component, inject, OnInit } from '@angular/core'
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common'
import { Router } from '@angular/router'
import { TableLazyLoadEvent, TableModule } from 'primeng/table'
import { TagModule } from 'primeng/tag'
import { CardModule } from 'primeng/card'
import { ButtonModule } from 'primeng/button'
import { TooltipModule } from 'primeng/tooltip'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { switchMap } from 'rxjs'
import {
  OptimizationDebugService,
  OptimizationRunSummary,
} from '../../services/optimization-debug.service'

@Component({
  selector: 'app-optimization-debug',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    DecimalPipe,
    TableModule,
    TagModule,
    CardModule,
    ButtonModule,
    TooltipModule,
    ProgressSpinnerModule,
  ],
  templateUrl: './optimization-debug.component.html',
  styleUrl: './optimization-debug.component.scss',
})
export class OptimizationDebugComponent implements OnInit {
  private service = inject(OptimizationDebugService)
  private router = inject(Router)

  public runs$ = this.service.runs$
  public total$ = this.service.total$
  public loading$ = this.service.loading$
  public actionLoading$ = this.service.actionLoading$

  private currentPage = 1
  private currentPerPage = 20

  ngOnInit() {
    this.service.getRuns().subscribe()
  }

  onLazyLoad(event: TableLazyLoadEvent) {
    this.currentPage =
      event.first !== undefined && event.rows
        ? Math.floor(event.first / event.rows) + 1
        : 1
    this.currentPerPage = event.rows ?? 20
    this.service.getRuns(this.currentPage, this.currentPerPage).subscribe()
  }

  refreshRuns() {
    this.service.getRuns(this.currentPage, this.currentPerPage).subscribe()
  }

  forceSyncAndRun() {
    this.service
      .syncPool()
      .pipe(switchMap(() => this.service.launchOptimization()))
      .subscribe({
        complete: () =>
          this.service.getRuns(1, this.currentPerPage).subscribe(),
      })
  }

  forceSyncAndRunAsync() {
    this.service
      .syncPool()
      .pipe(switchMap(() => this.service.launchOptimization(true)))
      .subscribe({
        complete: () =>
          this.service.getRuns(1, this.currentPerPage).subscribe(),
      })
  }

  openRun(run: OptimizationRunSummary) {
    this.router.navigate(['/admin/optimization-debug', run.id])
  }

  canCancel(run: OptimizationRunSummary): boolean {
    return run.status === 'pending' || run.status === 'running'
  }

  cancelRun(run: OptimizationRunSummary, event: MouseEvent) {
    event.stopPropagation()
    if (
      !confirm(`Annuler le run #${run.id} ? Cette action est irréversible.`)
    ) {
      return
    }
    this.service.cancelRun(run.id).subscribe({
      complete: () =>
        this.service.getRuns(this.currentPage, this.currentPerPage).subscribe(),
    })
  }

  getStatusSeverity(
    status: string
  ): 'success' | 'warn' | 'danger' | 'info' | 'secondary' {
    switch (status) {
      case 'completed':
        return 'success'
      case 'running':
        return 'info'
      case 'pending':
        return 'warn'
      case 'failed':
        return 'danger'
      case 'cancelled':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'completed':
        return 'Terminé'
      case 'running':
        return 'En cours'
      case 'pending':
        return 'En attente'
      case 'failed':
        return 'Échoué'
      case 'cancelled':
        return 'Annulé'
      default:
        return status
    }
  }

  formatDuration(seconds: number | null): string {
    if (seconds === null) return '—'
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return m > 0 ? `${m}m ${s}s` : `${s}s`
  }

  coveragePercent(run: OptimizationRunSummary): number | null {
    if (!run.affairsCount || !run.affairsWithSlotsCount) return null
    return Math.round((run.affairsWithSlotsCount / run.affairsCount) * 100)
  }
}
